import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiCall } from "@excellent/ui/api";
import {
  environments,
  type EnvironmentUiContextResponse,
} from "@excellent/ui/api/contracts/environments";
import {
  environmentContextExpired,
  useEnvironmentContextClock,
} from "@excellent/ui/blocks/excellent/environments/context-lifetime";
import {
  advanceCanonicalEnvironmentRequestGeneration,
  canonicalEnvironmentIntentKey,
  environmentUiContextQueryForSelection,
  hrefWithCanonicalEnvironmentSelection,
  parseCanonicalEnvironmentRouteSelection,
  type CanonicalEnvironmentSelectionIntent,
} from "./environment-navigation";
import {
  environmentViewerBindingSupported,
  signServerIssuedEnvironmentViewerChallenge,
} from "./environment-viewer-binding";

export type CanonicalEnvironmentIdentity = {
  authenticated: boolean;
  userId: number | null;
  handle: string | null;
  workspaceId: number | null;
  shellWorkspaceId: number | null;
};

export type CanonicalEnvironmentContextValue = {
  selection: CanonicalEnvironmentSelectionIntent;
  requestGeneration: number;
  /** Account, workspace, subject/set and observed authority epochs. */
  cacheKey: string | null;
  response: EnvironmentUiContextResponse | null;
  error: Error | null;
  isLoading: boolean;
  isValidating: boolean;
  invalidRoute: boolean;
  select: (selection: CanonicalEnvironmentSelectionIntent) => void;
  refresh: () => Promise<unknown>;
  loadMore: () => Promise<unknown>;
};

const CanonicalEnvironmentContext = createContext<CanonicalEnvironmentContextValue | null>(null);

export function canonicalEnvironmentIdentityKey(
  identity: CanonicalEnvironmentIdentity | null | undefined,
): string | null {
  if (
    !identity?.authenticated ||
    identity.userId == null ||
    identity.workspaceId == null ||
    identity.workspaceId !== identity.shellWorkspaceId
  )
    return null;
  return JSON.stringify([identity.userId, identity.handle, identity.workspaceId]);
}

export function responseMatchesSelection(
  response: EnvironmentUiContextResponse | undefined,
  selection: CanonicalEnvironmentSelectionIntent,
): response is EnvironmentUiContextResponse {
  if (
    !response ||
    response.requestScope !== (selection.kind === "environment" ? "environment" : selection.kind)
  )
    return false;
  // Non-disclosing setup/error states must remain visible for exact links too.
  if (response.status === "setup-required" || response.status === "error") return true;
  if (selection.kind !== "environment") return true;
  const subject =
    response.presentation?.subject ??
    (response.selection?.kind === "environment" ? response.selection.subject : null);
  return (
    subject?.originTenantId === selection.subject.originTenantId &&
    subject.environmentId === selection.subject.environmentId &&
    subject.generationId === selection.subject.generationId
  );
}

/**
 * The universal verification endpoints still accept the historical `env=real`
 * fixture selector. Keep that compatibility bridge deliberately narrow: it is
 * valid only after the canonical context has proved a current local target.
 * Remote, offline, unknown, setup, and expired contexts must not be silently
 * retargeted to the local ledger.
 */
export function canonicalLocalEnvironmentAvailable(
  context: CanonicalEnvironmentContextValue | null | undefined,
  now = Date.now(),
): boolean {
  const response = context?.response;
  return Boolean(
    context &&
      !context.error &&
      !context.invalidRoute &&
      response?.status === "ready" &&
      response.presentation?.locality.kind === "local" &&
      !environmentContextExpired(response, now),
  );
}

/** A canonical Work read can be scoped to any independently verified serving
 * Environment. Local remains the legacy adapter; remote and offline targets
 * require the server's exact durable Work attribution tuple. */
export function canonicalEnvironmentAvailable(
  context: CanonicalEnvironmentContextValue | null | undefined,
  now = Date.now(),
): boolean {
  const response = context?.response;
  return Boolean(
    context &&
      !context.error &&
      !context.invalidRoute &&
      response?.status === "ready" &&
      ["local", "remote", "offline-snapshot"].includes(
        response.presentation?.locality.kind ?? "",
      ) &&
      !environmentContextExpired(response, now),
  );
}

export function canonicalEnvironmentResponseKey(
  partition: string,
  response: EnvironmentUiContextResponse,
): string {
  const presentation = response.presentation;
  return JSON.stringify([
    partition,
    response.selection,
    presentation
      ? [
          presentation.subject,
          presentation.authority.authorityTenantId,
          presentation.authority.bindingDigest,
          presentation.authority.callerEpochDigest,
          presentation.authority.grantEpochDigest,
          presentation.viewerBinding?.state === "verified"
            ? presentation.viewerBinding.evidenceDigest
            : null,
        ]
      : response.evaluatedAt,
  ]);
}

function sameSelection(
  left: EnvironmentUiContextResponse["selection"],
  right: EnvironmentUiContextResponse["selection"],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function directoryAuthorityPin(response: EnvironmentUiContextResponse): string {
  const presentation = response.presentation;
  if (!presentation) {
    // Directory-only pages have no presentation tuple to pin. Their lease and
    // state still must remain identical; in particular, a transition to
    // setup/error may never inherit names from the prior page.
    return JSON.stringify({
      status: response.status,
      reasonCode: response.reasonCode,
      requestScope: response.requestScope,
      selection: response.selection,
      directoryAuthority: response.directoryAuthority,
    });
  }
  return JSON.stringify({
    status: response.status,
    requestScope: response.requestScope,
    selection: response.selection,
    directoryAuthority: response.directoryAuthority,
    validUntil: response.validUntil,
    presentation: {
      subject: presentation.subject,
      authority: presentation.authority,
      viewerBinding: presentation.viewerBinding,
      servingNode: presentation.servingNode,
      provenance: presentation.provenance,
    },
  });
}

export function mergeContextPages(
  previous: EnvironmentUiContextResponse,
  next: EnvironmentUiContextResponse,
): EnvironmentUiContextResponse {
  if (
    previous.requestScope !== next.requestScope ||
    !sameSelection(previous.selection, next.selection) ||
    directoryAuthorityPin(previous) !== directoryAuthorityPin(next) ||
    previous.status === "setup-required" ||
    previous.status === "error" ||
    next.status === "setup-required" ||
    next.status === "error"
  )
    throw new Error("environment-directory-context-mismatch");
  const seen = new Set<string>();
  const items = [...previous.directory.items, ...next.directory.items].filter((item) => {
    const key = JSON.stringify(item.subject);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const validUntil =
    previous.validUntil && next.validUntil
      ? Date.parse(previous.validUntil) <= Date.parse(next.validUntil)
        ? previous.validUntil
        : next.validUntil
      : next.validUntil;
  return {
    ...next,
    validUntil,
    directory: {
      ...next.directory,
      items,
      pageSize: Math.max(previous.directory.pageSize, next.directory.pageSize),
      omittedCount:
        previous.directory.omittedCount === null || next.directory.omittedCount === null
          ? null
          : Math.max(previous.directory.omittedCount, next.directory.omittedCount),
    },
  };
}

type ProviderProps = {
  cacheNamespace?: string;
  pathname: string;
  search: string;
  identity?: CanonicalEnvironmentIdentity | null;
  onNavigate?: (to: string) => void;
  children: ReactNode;
};

export function CanonicalEnvironmentProvider(props: ProviderProps) {
  const identityKey = canonicalEnvironmentIdentityKey(props.identity);
  const route = parseCanonicalEnvironmentRouteSelection(props.search);
  const selection = route.kind === "valid" ? route.selection : ({ kind: "default" } as const);
  const partition = JSON.stringify([
    identityKey,
    props.cacheNamespace,
    route.kind === "invalid" ? props.search : canonicalEnvironmentIntentKey(selection),
  ]);
  // A fresh mount owns a private single-entry cache. Returning to an old account
  // or target requires a fresh request; URL-keyed SWR/localStorage is never read.
  return (
    <CanonicalEnvironmentSession
      key={partition}
      {...props}
      partition={partition}
      identityKey={identityKey}
    />
  );
}

function CanonicalEnvironmentSession({
  pathname,
  search,
  onNavigate,
  children,
  partition,
  identityKey,
}: ProviderProps & { partition: string; identityKey: string | null }) {
  const route = useMemo(() => parseCanonicalEnvironmentRouteSelection(search), [search]);
  const invalidRoute = route.kind === "invalid";
  const selection = useMemo<CanonicalEnvironmentSelectionIntent>(
    () => (route.kind === "valid" ? route.selection : { kind: "default" }),
    [route],
  );
  const query = useMemo(() => environmentUiContextQueryForSelection(selection), [selection]);
  const [requestGeneration] = useState(advanceCanonicalEnvironmentRequestGeneration);
  const [entry, setEntry] = useState<{
    key: string;
    response: EnvironmentUiContextResponse;
  } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isValidating, setValidating] = useState(false);
  const sequence = useRef(0);
  const mounted = useRef(false);
  const viewerBindingSubject = useRef<{
    subjectKey: string;
    validUntil: string;
  } | null>(null);

  const ensureViewerBinding = useCallback(
    async (data: EnvironmentUiContextResponse): Promise<EnvironmentUiContextResponse> => {
      if (!environmentViewerBindingSupported()) return data;
      const subject =
        data.presentation?.subject ??
        (data.selection?.kind === "environment" ? data.selection.subject : null);
      if (!subject) return data;
      const subjectKey = JSON.stringify(subject);
      if (data.presentation?.viewerBinding.state === "verified") {
        viewerBindingSubject.current = {
          subjectKey,
          validUntil: data.presentation.viewerBinding.validUntil,
        };
        return data;
      }
      if (
        viewerBindingSubject.current?.subjectKey === subjectKey &&
        Date.parse(viewerBindingSubject.current.validUntil) > Date.now()
      )
        return data;
      if (viewerBindingSubject.current?.subjectKey === subjectKey)
        viewerBindingSubject.current = null;
      try {
        const challenge = await apiCall(environments.viewerChallenge, { body: { subject } });
        const signatureBase64Url = await signServerIssuedEnvironmentViewerChallenge(
          challenge.challengeBytes,
        );
        if (!signatureBase64Url) return data;
        await apiCall(environments.viewerBinding, {
          body: { challengeBytes: challenge.challengeBytes, signatureBase64Url },
        });
        const rebound = await apiCall(environments.uiContext, { query });
        if (rebound.presentation?.viewerBinding.state === "verified") {
          viewerBindingSubject.current = {
            subjectKey,
            validUntil: rebound.presentation.viewerBinding.validUntil,
          };
        }
        return rebound;
      } catch {
        // A plain browser, an unavailable installation key, or an unprovisioned
        // authority must remain an honest unknown locality, not a failed shell.
        return data;
      }
    },
    [query],
  );

  const refresh = useCallback(async () => {
    if (!mounted.current || !identityKey || invalidRoute) return;
    const request = ++sequence.current;
    setValidating(true);
    try {
      const data = await ensureViewerBinding(await apiCall(environments.uiContext, { query }));
      if (!mounted.current || request !== sequence.current) return;
      if (!responseMatchesSelection(data, selection))
        throw new Error("environment-context-mismatch");
      if (data.status === "error") throw new Error(data.reasonCode);
      if (environmentContextExpired(data)) throw new Error("environment-context-expired");
      setEntry({ key: canonicalEnvironmentResponseKey(partition, data), response: data });
      setError(null);
    } catch (cause) {
      if (!mounted.current || request !== sequence.current) return;
      setEntry(null);
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      if (mounted.current && request === sequence.current) setValidating(false);
    }
  }, [ensureViewerBinding, identityKey, invalidRoute, partition, query, selection]);

  const loadMore = useCallback(async () => {
    if (!mounted.current || !identityKey || invalidRoute || !entry?.response.directory.nextCursor)
      return;
    const request = ++sequence.current;
    setValidating(true);
    try {
      const data = await apiCall(environments.uiContext, {
        query: { ...query, cursor: entry.response.directory.nextCursor },
      });
      if (!mounted.current || request !== sequence.current) return;
      if (!responseMatchesSelection(data, selection))
        throw new Error("environment-context-mismatch");
      if (data.status === "error") throw new Error(data.reasonCode);
      const merged = mergeContextPages(entry.response, data);
      if (environmentContextExpired(merged)) throw new Error("environment-context-expired");
      setEntry({ key: canonicalEnvironmentResponseKey(partition, merged), response: merged });
      setError(null);
    } catch (cause) {
      if (!mounted.current || request !== sequence.current) return;
      setEntry(null);
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      if (mounted.current && request === sequence.current) setValidating(false);
    }
  }, [entry, identityKey, invalidRoute, partition, query, selection]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    const revalidate = () => {
      if (document.visibilityState !== "hidden") void refresh();
    };
    const timer = setInterval(revalidate, 15_000);
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => {
      mounted.current = false;
      sequence.current++;
      clearInterval(timer);
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
    };
  }, [refresh]);

  const now = useEnvironmentContextClock(entry?.response ?? null);
  const expired = entry !== null && environmentContextExpired(entry.response, now);
  useEffect(() => {
    if (expired) setEntry(null);
  }, [expired]);
  const response =
    !invalidRoute && identityKey && !expired && !error ? (entry?.response ?? null) : null;
  const select = useCallback(
    (next: CanonicalEnvironmentSelectionIntent) => {
      sequence.current++;
      setEntry(null);
      advanceCanonicalEnvironmentRequestGeneration();
      onNavigate?.(hrefWithCanonicalEnvironmentSelection(`${pathname}${search}`, next));
    },
    [onNavigate, pathname, search],
  );
  const value: CanonicalEnvironmentContextValue = {
    selection,
    requestGeneration,
    cacheKey: response ? entry!.key : null,
    response,
    error: invalidRoute
      ? new Error(route.kind === "invalid" ? route.reason : "invalid-environment-scope")
      : error,
    isLoading: Boolean(identityKey && !invalidRoute && !response && !error),
    isValidating,
    invalidRoute,
    select,
    refresh,
    loadMore,
  };
  return (
    <CanonicalEnvironmentContext.Provider value={value}>
      {children}
    </CanonicalEnvironmentContext.Provider>
  );
}

export function useCanonicalEnvironmentOptional(): CanonicalEnvironmentContextValue | null {
  return useContext(CanonicalEnvironmentContext);
}

export function useCanonicalEnvironment(): CanonicalEnvironmentContextValue {
  const value = useCanonicalEnvironmentOptional();
  if (!value)
    throw new Error("useCanonicalEnvironment must be used inside CanonicalEnvironmentProvider");
  return value;
}
